import React, { useState, useEffect, useContext } from 'react'
import { GongIcon } from '../../icons/Gong'
import { AuthContext } from '../../../context/AuthContext'

interface GongImportDialogProps {
  handleClose: (...args: any[]) => any
  handleImport: (...args: any[]) => any
}

export function GongImportDialog({ handleClose, handleImport }: GongImportDialogProps) {
  const [selectedOption, setSelectedOption] = useState('7days')
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const { user } = useContext(AuthContext)

  // Handle closing with animation
  const closeWithAnimation = () => {
    setIsVisible(false)
    setTimeout(() => {
      handleClose()
    }, 300) // Match this with the transition duration
  }

  // Force focus on the dialog when it mounts
  useEffect(() => {
    // Add a class to the body to prevent scrolling
    document.body.classList.add('overflow-hidden')

    // Trigger entrance animation
    setTimeout(() => {
      setIsVisible(true)
    }, 50)

    // Check if workspace is selected
    if (!user.active_workspace) {
      setError('No active workspace selected. Please select a workspace first.')
    }

    // Remove the class when the component unmounts
    return () => {
      document.body.classList.remove('overflow-hidden')
    }
  }, [user.active_workspace])

  const importOptions = [
    { id: 'future', label: 'Only future calls', days: 0 },
    { id: '7days', label: 'Last 7 Days', days: 7 },
    { id: '30days', label: 'Last 30 Days', days: 30 },
    { id: '90days', label: 'Last 90 Days', days: 90 },
  ]

  // Handle the import with loading state
  const handleImportWithLoading = async () => {
    try {
      // Validate workspace is selected
      if (!user.active_workspace) {
        setError('No active workspace selected. Please select a workspace first.')
        return
      }

      setError(null)
      setIsLoading(true)
      const selectedDays = importOptions.find((opt) => opt.id === selectedOption).days

      // Call the parent's handleImport function and wait for response
      const success = await handleImport(selectedDays)

      if (success) {
        // Success toast is now shown by the parent after API responds with 200
        // Close the dialog after a short delay to show loading state
        setTimeout(() => {
          closeWithAnimation()
        }, 500)
      } else {
        throw new Error('Import failed. Please try again.')
      }
    } catch (error) {
      console.error('Error importing Gong calls:', error)
      setError(error.message || 'Failed to import Gong calls')
      setIsLoading(false)
    }
  }

  return (
    <div
      className={`fixed inset-0 bg-black z-50 flex items-center justify-center transition-all duration-300 ease-in-out ${
        isVisible ? 'bg-opacity-60 backdrop-blur-sm' : 'bg-opacity-0 backdrop-blur-none'
      }`}
      onClick={isLoading ? null : closeWithAnimation}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }} /* Ensure dialog covers entire viewport */
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-[650px] px-[40px] py-10 bg-white rounded-[24px] shadow-2xl border border-gray-100 transition-all duration-300 ease-in-out ${
          isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
        }`}
        style={{ boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)' }}
      >
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-16 h-16 mb-8">
              <div className="w-full h-full rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin"></div>
            </div>
            <h2 className="text-zinc-800 text-[26px] font-bold font-['Manrope'] leading-[32px] text-center mb-4">
              Starting Gong Import
            </h2>
            <p className="text-zinc-600 text-base font-normal font-['Manrope'] text-center max-w-md">
              We're preparing to import your Gong calls. The process will continue in the background after this dialog
              closes.
            </p>
          </div>
        ) : (
          <>
            {/* Gong logo at the top */}
            <div className="flex justify-center mb-8">
              <div className="w-10 h-10">
                <GongIcon />
              </div>
            </div>

            <header className="flex items-center justify-between mb-4">
              <h2 className="text-zinc-800 text-[26px] font-bold font-['Manrope'] leading-[32px] text-center w-full">
                Import Gong Calls
              </h2>
              <button
                onClick={closeWithAnimation}
                className="hover:bg-gray-100 p-2 rounded-full transition-colors duration-200 absolute top-6 right-6"
                aria-label="Close dialog"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18" stroke="#40424A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M6 6L18 18" stroke="#40424A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </header>

            <p className="text-zinc-600 text-base font-normal font-['Manrope'] mb-8 text-center">
              Choose which Gong calls you'd like to import and analyze with Evermuse
            </p>

            <div className="mb-8">
              <p className="text-zinc-700 text-lg font-medium font-['Manrope'] mb-6">Select time period:</p>
              <ul className="space-y-3">
                {importOptions.map((option) => (
                  <li
                    key={option.id}
                    className={`flex items-center p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                      selectedOption === option.id
                        ? 'bg-blue-50 border border-blue-200'
                        : 'border border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => setSelectedOption(option.id)}
                  >
                    <div className="flex items-center">
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center mr-2`}
                        style={{
                          backgroundColor: selectedOption === option.id ? '#3b82f6' : 'white',
                          borderColor: selectedOption === option.id ? '#3b82f6' : '#9CA3AF',
                          borderWidth: '2px',
                          borderStyle: 'solid',
                        }}
                      >
                        {selectedOption === option.id && <div className="w-2.5 h-2.5 rounded-full bg-white"></div>}
                      </div>
                    </div>
                    <span
                      className={`ml-2 text-base font-medium font-['Manrope'] ${
                        selectedOption === option.id ? 'text-blue-800' : 'text-zinc-700'
                      }`}
                    >
                      {option.label}
                    </span>
                    {option.id === '7days' && (
                      <span className="ml-auto bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                        Recommended
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <div className="text-amber-800 text-base font-normal font-['Manrope']">
                  <span className="font-semibold">Note:</span> Please note imported calls count towards your usage just
                  like new calls. Gong calls will be imported in the background. This process may take several minutes
                  depending on the number of calls.
                </div>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
                <div className="flex items-start">
                  <svg
                    className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <p className="text-red-800 text-base font-normal font-['Manrope']">{error}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 justify-center">
              <button
                onClick={closeWithAnimation}
                className="px-6 py-3 rounded-lg justify-center items-center gap-3 inline-flex hover:bg-gray-50 transition-colors duration-200 border border-gray-200"
                disabled={isLoading}
              >
                <p className="text-gray-700 text-base font-medium font-['Manrope']">Cancel</p>
              </button>
              <button
                onClick={handleImportWithLoading}
                className="px-8 py-3 bg-blue-600 rounded-lg shadow-sm justify-center items-center gap-2 inline-flex hover:bg-blue-700 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed border border-blue-600"
                disabled={isLoading || !user.active_workspace || error !== null || user.id !== '109954400808152224705'}
              >
                <p className="text-black text-base font-semibold font-['Manrope']">Start Import</p>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
